import { Box, Stack, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../../assets";
import AppLoader from "../../../../components/AppLoader";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import appcolors from "../../../../constants/Colors";
import { CURRENCY, PLAN_DELIVERY_TYPES } from "../../../../constants/Variables";
import useDebounce from "../../../../hooks/useDebounce";
import {
    setUserPlanCurrentPage,
    setUserPlanPageLimit,
    setUserPlanTotalCount,
    setUserPlanTotalPages,
} from "../../../../lib/slices/pagination/userPlanPageSlice";
import {
    setUsersPlanEndDate,
    setUsersPlanName,
    setUsersPlanStartDate,
} from "../../../../lib/slices/searchKeysSlice";
import { deletePlan, getAllPlans, getPlanDashboard } from "../../../../services/planApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { UsersPlansFilters } from "../Filters";
import { deleteConfirmAlert } from "../../../../components/Alert";
import { useNavigate } from "react-router-dom";

const headerData = [
    "createdAt",
    "user",
    "name",
    "deliveryFrequency",
    "nextDeliveryDate",
    "validityPeriod",
    "status",
    "actions",
];

const tableRows = [
    "created_date",
    "plan_created_by",
    "plan_name",
    "delivery_frequency",
    "next_delivery_date",
    "validity_period",
    "plan_status",
    "actions",
];

export const UserPlan = ({ handleChangeTab }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState({
        allPlans: false,
        planDashboard: false,
        deletePlan: false,
    });
    const [allPlans, setAllPlans] = useState([]);
    const [planDashboard, setPlanDashboard] = useState(null);
    const { t } = useTranslation(["products", "common"]);
    const { currentLanguage } = useSelector((state) => state.languageReducer);
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    const pagination = useSelector((state) => state.userPlanPaginationReducer);
    const debouncedSearchTerm = useDebounce(
        searchKeys.usersPlanName,
        1000,
        true,
        () => {
            dispatch(setUserPlanCurrentPage(1));
        },
    );

    const fetchAllPlans = async (search, page, limit, startDate, endDate) => {
        try {
            setIsLoading({ ...isLoading, allPlans: true });
            const params = {
                search: search,
                page: page,
                limit: limit,
                startDate: startDate,
                endDate: endDate,
            };
            const response = await getAllPlans(params);
            const pagination = response?.data.data.pagination;
            if (response.data.status === "success") {
                let data = response?.data.data.data;
                setAllPlans(data);
                dispatch(setUserPlanPageLimit(pagination.limit));
                dispatch(setUserPlanCurrentPage(pagination.page));
                dispatch(setUserPlanTotalCount(pagination.total));
                dispatch(setUserPlanTotalPages(pagination.totalPages));
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading({ ...isLoading, allPlans: false });
        }
    };

    const fetchPlanDashboard = async (startDate, endDate) => {
        try {
            setIsLoading({ ...isLoading, planDashboard: true });
            const query = {
                startDate,
                endDate,
            };
            const response = await getPlanDashboard(query);
            if (response.data.status === "success") {
                setPlanDashboard(response.data.data);
            } else {
                console.error("response", response.data.message);
            }
        } catch (error) {
            console.error("error", error.message);
        } finally {
            setIsLoading({ ...isLoading, planDashboard: false });
        }
    };

    useEffect(() => {
        fetchAllPlans(
            debouncedSearchTerm,
            pagination.page,
            pagination.limit,
            searchKeys.usersPlanStartDate,
            searchKeys.usersPlanEndDate,
        );
    }, [
        debouncedSearchTerm,
        pagination.page,
        pagination.limit,
        // searchKeys.usersPlanStartDate,
        // searchKeys.usersPlanEndDate,
    ]);

    useEffect(() => {
        fetchPlanDashboard(
            searchKeys.usersPlanStartDate,
            searchKeys.usersPlanEndDate,
        );
    }, []);

    const prepareExportData = () => {
        return allPlans.map((item) => ({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
            "Plan Name": item.name || "-",
            "User Name": item.user?.firstname + " " + item.user?.lastname || "-",
            "User Email": item.user?.email || "-",
            "User Contact": item.user?.contact || "-",
            "Delivery Frequency":
                PLAN_DELIVERY_TYPES.find((x) => x.value === item.deliveryFrequency)
                    ?.label[currentLanguage] || "-",
            "Next Delivery Date":
                moment(item.nextDeliveryAt).format("MM-DD-YYYY") || "-",
            "Validity Period":
                moment(item.validityPeriod).format("MM-DD-YYYY") || "-",
            Status:
                item.status?.slice(0, 1).toUpperCase() + item.status?.slice(1) || "-",
        }));
    };

    const handleDeletePlan = async (row) => {

        deleteConfirmAlert({
            title: t("deleteConfirmation", { ns: "common" }),
            text: t("deleteConfirmationText", { ns: "common" }),
            confirmButtonText: t("delete", { ns: "common" }),
            cancelButtonText: t("cancel", { ns: "common" }),
        }).then(async (result) => {
            if (result) {
                try {
                    setIsLoading((prev) => ({ ...prev, deletePlan: true }));
                    const response = await deletePlan(row?.row?._id);
                    if (response?.data?.status === "success") {
                        fetchPlanDashboard(
                            debouncedSearchTerm,
                            pagination.page,
                            pagination.limit,
                            searchKeys.usersPlanStartDate,
                            searchKeys.usersPlanEndDate,
                        );
                        fetchAllPlans(
                            debouncedSearchTerm,
                            pagination.page,
                            pagination.limit,
                            searchKeys.usersPlanStartDate,
                            searchKeys.usersPlanEndDate,
                        );
                        handleOpenSnackBar("success", response?.data?.message);
                    } else {
                        handleOpenSnackBar("error", response?.data?.message);
                    }
                } catch (error) {
                    handleOpenSnackBar("error", error?.message);
                } finally {
                    setIsLoading((prev) => ({ ...prev, deletePlan: false }));
                }
            }
        });
    };

    const renderHeaderAction = () => {
        const handleResetFilters = () => {
            dispatch(setUsersPlanName(""));
            dispatch(setUsersPlanStartDate(null));
            dispatch(setUsersPlanEndDate(null));
            dispatch(setUserPlanCurrentPage(1));

            fetchAllPlans("", 1, pagination.limit, null, null);
            fetchPlanDashboard(null, null);
        };

        return (
            <Stack
                gap={1}
                flexGrow={1}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-end"}
            >
                <GlobalButton
                    variant={"export_button"}
                    btnText={""}
                    startIcon={<RefreshIcon fontSize="small" />}
                    handleClickBtn={handleResetFilters}
                    styleProps={{
                        width: 40,
                        minWidth: 40,
                        height: 40,
                        p: 0,
                        "& .MuiButton-startIcon": {
                            margin: 0,
                        },
                    }}
                />
                <CSVLink
                    data={prepareExportData()}
                    filename={"users-plans.csv"}
                    className="csv-link"
                    target="_blank"
                >
                    <GlobalButton
                        variant={"export_button"}
                        btnText={"export"}
                        startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
                        styleProps={{
                            width: "auto",
                            height: 40,
                        }}
                    />
                </CSVLink>
            </Stack>
        );
    };

    const handlePageChange = (event, newPage) => {
        const page = newPage + 1;
        dispatch(setUserPlanCurrentPage(page));
    };

    const handleRowsPerPageChange = (event) => {
        const limit = parseInt(event.target.value, 10);
        dispatch(setUserPlanPageLimit(limit));
        dispatch(setUserPlanCurrentPage(1));
    };

    const handleViewDetail = (row, index) => {
        navigate(`/products/user-plan/${row?.row?._id}`);
    };

    const renderTable = useMemo(() => {
        return (
            <DynamicPaginatedTable
                minWidth={1200}
                tableHeader={headerData}
                displayRows={tableRows}
                tableData={allPlans || []}
                showDynamicPagination={true}
                paginatedData={pagination}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                showViewDetail={true}
                handleViewDetail={handleViewDetail}
                showActions={true}
                showDelete={true}
                handleDelete={handleDeletePlan}
                isLoading={isLoading.allPlans}
            />
        );
    }, [allPlans, isLoading.allPlans]);

    const renderPlanDashboard = useMemo(() => {
        return (
            <Stack
                mb={2}
                p={2.2}
                borderRadius={"14px"}
                boxShadow={"0px 8px 22px rgba(21, 112, 239, 0.16)"}
                sx={{
                    background: "linear-gradient(135deg, #EEF5FF 0%, #F7FBFF 100%)",
                    border: "1px solid #DDEBFF",
                }}
                maxWidth={340}
            >
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                >
                    <Box>
                        <Typography variant={"body2"} color={appcolors.crayolaGrey}>
                            {t("totalDiscountGiven")}
                        </Typography>
                        <Box mt={0.5}>
                            <Typography variant={"h5"} fontWeight={700} color={"#0D47A1"}>
                                {planDashboard?.totalDiscountAmount || 0} {CURRENCY}
                            </Typography>
                        </Box>
                    </Box>

                    <Stack
                        width={44}
                        height={44}
                        borderRadius={"50%"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        sx={{
                            background: "linear-gradient(135deg, #1570EF 0%, #4C9AFF 100%)",
                            boxShadow: "0px 6px 14px rgba(21, 112, 239, 0.28)",
                        }}
                    >
                        <Typography
                            variant={"body1"}
                            color={appcolors.white}
                            fontWeight={800}
                        >
                            %
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>
        );
    }, [planDashboard]);


    const handleFetchData = () => {
        dispatch(setUserPlanCurrentPage(1));

        fetchAllPlans(
            debouncedSearchTerm,
            pagination.page,
            pagination.limit,
            searchKeys.usersPlanStartDate,
            searchKeys.usersPlanEndDate,
        );

        fetchPlanDashboard(
            searchKeys.usersPlanStartDate,
            searchKeys.usersPlanEndDate,
        );
    };

    return (
        <React.Fragment>
            {/* <AppLoader isVisible={isLoading.allPlans || isLoading.planDashboard || isLoading.deletePlan} /> */}
            <PaperContainer
                title={t("usersPlansList")}
                subtitle={t("usersPlansDescription")}
                headerActions={renderHeaderAction()}
                searchActions={<UsersPlansFilters fetchData={handleFetchData} />}
            >
                {renderPlanDashboard}
                {renderTable}
            </PaperContainer>
        </React.Fragment>
    );
}
